import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { loadStripe } from '@stripe/stripe-js';
import StripeElements from './StripeElements';
import { RootState } from '../../../../../redux/root';
import { PermitInvoiceType } from '../../../../../Components/InvoiceTool/types';
import StripeInvoicesTable from './StripeInvoicesTable';
import { getFranchiseStripeList } from '../../../../../api/franchise';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { Loader } from '../../../../../atoms';

type Props = {
  onCancel: () => void;
};

const PayPopup: FC<Props> = ({ onCancel }) => {
  const [stripePromise, setStripePromise] = useState<any>();
  const email = useSelector((s: RootState) => s.userAuth.info.email);
  const { selectedProjectInvoice, totalStripeAmount, selectedInvoicesDataToPay } = useSelector((s: RootState) => s.fees);
  const [permit, setPermit] = useState<PermitInvoiceType>({} as PermitInvoiceType);

  // EA-1344: the PaymentIntent is now created inside CheckoutForm once the card
  // country (and therefore the processing fee) is known. Here we only load the
  // franchise's Stripe publishable key.
  useEffect(() => {
    const loadStripeKey = async () => {
      // @ts-ignore
      const result = await getFranchiseStripeList(permit.franchiseId);
      const pk = _.get(result, 'data.franchiseStripeSetting.stripePublishableKey');
      setStripePromise(loadStripe(pk));
    };
    if (permit.id) {
      loadStripeKey();
    }
  }, [permit]);

  useEffect(() => {
    if (selectedProjectInvoice) {
      setPermit(selectedProjectInvoice.permit);
    }
  }, [selectedProjectInvoice]);

  // Comma-joined invoice numbers for the PaymentIntent metadata.
  const invoiceNumbers = selectedInvoicesDataToPay
    .map(inv => {
      if (inv.invoiceNumber.indexOf('----') > -1) {
        return inv.invoiceNumber.split('----')[0];
      }
      return inv.invoiceNumber;
    })
    .join(',');

  const ready = stripePromise && totalStripeAmount > 0;

  return (
    <Wrapper>
      <Grid>
        <SummaryColumn>
          <StripeInvoicesTable />
        </SummaryColumn>
        <PaymentColumn>
          <SectionLabel>Payment Details</SectionLabel>
          <PaymentCard>
            {ready ? (
              <StripeElements
                stripePromise={stripePromise}
                amount={totalStripeAmount}
                permitId={permit.id}
                invoiceNumber={invoiceNumbers}
                email={email}
                onCancel={onCancel}
              />
            ) : (
              <LoadingState>
                <Loader />
                <span>Preparing secure payment…</span>
              </LoadingState>
            )}
          </PaymentCard>
          <SecureNote>
            <LockOutlinedIcon style={{ fontSize: 14 }} />
            <span>Payments are securely processed by Stripe. Your card details never touch our servers.</span>
          </SecureNote>
        </PaymentColumn>
      </Grid>
    </Wrapper>
  );
};

export default PayPopup;

const Wrapper = styled.div`
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif;
  color: var(--mainText);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
`;

const SummaryColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const PaymentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

const SectionLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin-bottom: 2px;
`;

const PaymentCard = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 22px;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;

  @media only screen and (max-width: 540px) {
    padding: 16px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  min-height: 320px;
  font-size: 0.875rem;
  color: var(--neutral-600);
`;

const SecureNote = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  color: var(--neutral-500);
  line-height: 1.45;

  svg {
    color: var(--neutral-500);
    flex-shrink: 0;
  }
`;
